// BETA 14.06 / 14.07 — Barrel Calibrator
// Modal tool for marking the exact barrel-tip position(s) on each weapon's
// sprite so PS Major can tune barrelLength accurately. User picks a weapon,
// clicks where each barrel tip should be (multiple clicks for multi-barrel
// weapons like Ex Girlfriend's 5-fan), copies the resulting JSON, pastes
// it back to PS Major in chat. Game pauses while open.

import { useEffect, useMemo, useRef, useState } from 'react';
import { WEAPON_DATABASE } from '../game/weapons';
import { setLabCalibratorOpen } from '../game/weaponLab';

interface Props {
  onClose: () => void;
}

const CANVAS_W = 720;
const CANVAS_H = 480;
const SLOT_X = CANVAS_W / 2;
const SLOT_Y = CANVAS_H / 2;
// How tall (in canvas px) we render the sprite. The in-game spriteSize is in
// world units; here we just want the sprite BIG so the user can click precisely.
const SPRITE_DISPLAY_PX = 240;

interface Dot { x: number; y: number; }

/** Cached image loader — keeps repeated weapon switches snappy. */
const _imgCache = new Map<string, HTMLImageElement>();
function getOrLoadImage(url: string): HTMLImageElement {
  let img = _imgCache.get(url);
  if (img) return img;
  img = new Image();
  img.src = url;
  _imgCache.set(url, img);
  return img;
}

export default function BarrelCalibrator({ onClose }: Props) {
  // Filter to weapons that have a visible orbital sprite (skip auras, arcs without icons).
  const allWeapons = useMemo(
    () => WEAPON_DATABASE.filter(w => w.spriteUrl && w.spriteUrl.length > 0),
    []
  );

  const [weaponId, setWeaponId] = useState<string>(allWeapons[0]?.id ?? '');
  const [dots, setDots] = useState<Dot[]>([]);
  const [copied, setCopied] = useState(false);
  // BETA 14.18 — Live rotate + scale sliders. User picks the angle that makes
  // the barrel point to the right (+X) in the canvas, and a scale multiplier
  // for the visible sprite size. Both values get included in the COPY JSON
  // so PS Major can apply them as spriteRotationOffset + spriteScale.
  const [rotationDeg, setRotationDeg] = useState<number>(0);
  const [scaleFactor, setScaleFactor] = useState<number>(1.0);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const weapon = allWeapons.find(w => w.id === weaponId) ?? allWeapons[0];

  // BETA 14.07 — pause the game while the calibrator is open. weaponLab.ts
  // exposes a setLabCalibratorOpen flag that engine.ts checks each frame and
  // gates its update() loop on. Reset on unmount.
  useEffect(() => {
    setLabCalibratorOpen(true);
    return () => setLabCalibratorOpen(false);
  }, []);

  // BETA 14.07 — track sprite-load state per weapon so the paint effect (which
  // lists this in its deps) re-runs when the async image finishes loading.
  const [spriteLoaded, setSpriteLoaded] = useState(false);
  useEffect(() => {
    if (!weapon) { setSpriteLoaded(false); return; }
    const img = getOrLoadImage(weapon.spriteUrl);
    if (img.complete && img.naturalWidth > 0) {
      setSpriteLoaded(true);
      return;
    }
    setSpriteLoaded(false);
    const onLoad = () => setSpriteLoaded(true);
    const onError = () => setSpriteLoaded(false);
    img.addEventListener('load', onLoad);
    img.addEventListener('error', onError);
    return () => {
      img.removeEventListener('load', onLoad);
      img.removeEventListener('error', onError);
    };
  }, [weapon]);

  // Paint the canvas: grid + slot crosshair + weapon sprite + numbered dots.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !weapon) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    void spriteLoaded; // keep dep — paint re-fires when sprite loads

    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

    // Background
    ctx.fillStyle = 'rgba(8, 10, 14, 1)';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    // Grid (every 20px, slightly thicker every 100px)
    for (let x = 0; x <= CANVAS_W; x += 20) {
      ctx.strokeStyle = x % 100 === 0 ? 'rgba(80, 200, 255, 0.18)' : 'rgba(80, 200, 255, 0.07)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x + 0.5, 0); ctx.lineTo(x + 0.5, CANVAS_H);
      ctx.stroke();
    }
    for (let y = 0; y <= CANVAS_H; y += 20) {
      ctx.strokeStyle = y % 100 === 0 ? 'rgba(80, 200, 255, 0.18)' : 'rgba(80, 200, 255, 0.07)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, y + 0.5); ctx.lineTo(CANVAS_W, y + 0.5);
      ctx.stroke();
    }

    // Weapon sprite — centered at (SLOT_X, SLOT_Y) with optional live rotate
    // + scale (BETA 14.18). User drags sliders to align the barrel with +X
    // and dial in a sprite scale; the values get exported in the COPY JSON.
    const img = getOrLoadImage(weapon.spriteUrl);
    let actualSprW = 0;
    let actualSprH = 0;
    if (img.complete && img.naturalWidth > 0) {
      const sprH = SPRITE_DISPLAY_PX * scaleFactor;
      const aspect = img.naturalWidth / img.naturalHeight;
      const sprW = sprH * aspect;
      actualSprW = sprW;
      actualSprH = sprH;
      ctx.save();
      ctx.translate(SLOT_X, SLOT_Y);
      ctx.rotate((rotationDeg * Math.PI) / 180);
      ctx.drawImage(img, -sprW / 2, -sprH / 2, sprW, sprH);
      ctx.restore();
    } else {
      ctx.fillStyle = 'rgba(127, 220, 255, 0.5)';
      ctx.font = '14px "Share Tech Mono", monospace';
      ctx.textAlign = 'center';
      ctx.fillText('loading sprite…', SLOT_X, SLOT_Y);
    }

    // Sprite bounding box (cyan dashed)
    if (actualSprW > 0) {
      ctx.strokeStyle = 'rgba(127, 220, 255, 0.5)';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(SLOT_X - actualSprW / 2, SLOT_Y - actualSprH / 2, actualSprW, actualSprH);
      ctx.setLineDash([]);
    }

    // Slot center crosshair (cyan plus + label)
    ctx.strokeStyle = '#00ffff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(SLOT_X - 18, SLOT_Y); ctx.lineTo(SLOT_X + 18, SLOT_Y);
    ctx.moveTo(SLOT_X, SLOT_Y - 18); ctx.lineTo(SLOT_X, SLOT_Y + 18);
    ctx.stroke();
    ctx.fillStyle = '#00ffff';
    ctx.font = '11px "Share Tech Mono", monospace';
    ctx.textAlign = 'left';
    ctx.fillText('SPRITE CENTER', SLOT_X + 22, SLOT_Y - 8);

    // ── Numbered dots (barrel 1, 2, 3...) ──
    // BETA 14.07: support multiple barrels per weapon (e.g. Ex Girlfriend's
    // 5-fan piercing). Each click adds another dot.
    dots.forEach((dot, i) => {
      const num = i + 1;
      // Dashed line from sprite center to dot
      ctx.strokeStyle = 'rgba(255, 80, 100, 0.7)';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(SLOT_X, SLOT_Y);
      ctx.lineTo(dot.x, dot.y);
      ctx.stroke();
      ctx.setLineDash([]);

      // Red dot with white outline
      ctx.fillStyle = '#ff3344';
      ctx.beginPath();
      ctx.arc(dot.x, dot.y, 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(dot.x, dot.y, 8, 0, Math.PI * 2);
      ctx.stroke();

      // Number inside the dot
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 11px "Share Tech Mono", monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(num), dot.x, dot.y);
      ctx.textBaseline = 'alphabetic';

      // Δ label
      const dx = dot.x - SLOT_X;
      const dy = dot.y - SLOT_Y;
      ctx.fillStyle = '#ff8899';
      ctx.font = '11px "Share Tech Mono", monospace';
      ctx.textAlign = 'left';
      ctx.fillText(`#${num}: Δ ${dx > 0 ? '+' : ''}${dx}, ${dy > 0 ? '+' : ''}${dy}`, dot.x + 14, dot.y - 10);
    });
  }, [weapon, dots, spriteLoaded, rotationDeg, scaleFactor]);

  function handleCanvasClick(e: React.MouseEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = Math.round((e.clientX - rect.left) * scaleX);
    const y = Math.round((e.clientY - rect.top) * scaleY);
    setDots(prev => [...prev, { x, y }]);
    setCopied(false);
  }

  function buildCopyJson(): string {
    if (!weapon || dots.length === 0) return '';
    const img = getOrLoadImage(weapon.spriteUrl);
    const spriteRenderH = SPRITE_DISPLAY_PX;
    const spriteRenderW = img.naturalWidth > 0
      ? SPRITE_DISPLAY_PX * (img.naturalWidth / img.naturalHeight)
      : SPRITE_DISPLAY_PX;
    const pxPerWorldUnit = SPRITE_DISPLAY_PX / Math.max(1, weapon.spriteSize);

    // Per-barrel breakdown
    const barrels = dots.map((d, i) => {
      const dxPx = d.x - SLOT_X;
      const dyPx = d.y - SLOT_Y;
      const dxWorld = dxPx / pxPerWorldUnit;
      const dyWorld = dyPx / pxPerWorldUnit;
      const fracX = spriteRenderW > 0 ? dxPx / spriteRenderW : 0;
      const fracY = spriteRenderH > 0 ? dyPx / spriteRenderH : 0;
      return {
        barrelNumber: i + 1,
        canvasPx: { x: d.x, y: d.y },
        offsetFromCenterPx: { dx: dxPx, dy: dyPx },
        offsetFromCenterWorldUnits: { dx: Number(dxWorld.toFixed(2)), dy: Number(dyWorld.toFixed(2)) },
        fractionOfSprite: { x: Number(fracX.toFixed(4)), y: Number(fracY.toFixed(4)) },
        // BETA 22.69 — SOCKET fraction: absolute PNG fraction from the sprite's
        // top-left (calibrator draws centered, so 0.5 + center-relative frac).
        // Paste straight into WEAPON_VISUALS muzzleFx/muzzleFy or a sockets.* pair.
        socketFraction: { fx: Number((0.5 + fracX).toFixed(4)), fy: Number((0.5 + fracY).toFixed(4)) },
        suggestedBarrelLength: Number(dxWorld.toFixed(2)),
        suggestedBarrelOffsetY: Number(dyWorld.toFixed(2)),
      };
    });

    const data = {
      tool: 'BarrelCalibrator',
      version: 'BETA 14.07',
      weapon: {
        id: weapon.id,
        name: weapon.name,
        spriteUrl: weapon.spriteUrl,
        spriteSize: weapon.spriteSize,
        currentBarrelLength: weapon.barrelLength,
        currentBaseProjectileCount: weapon.baseProjectileCount,
        currentSpreadAngle: weapon.spreadAngle,
        spriteFacesLeft: weapon.spriteFacesLeft ?? false,
      },
      sprite: {
        naturalWidth: img.naturalWidth || null,
        naturalHeight: img.naturalHeight || null,
      },
      calibrator: {
        canvasWidth: CANVAS_W,
        canvasHeight: CANVAS_H,
        spriteCenterPx: { x: SLOT_X, y: SLOT_Y },
        spriteDisplayHeightPx: SPRITE_DISPLAY_PX,
        pxPerWorldUnit,
      },
      // BETA 14.18 — User-tuned sprite transform. PS Major applies these as
      // weapon.spriteRotationOffset (radians) + weapon.spriteScale (multiplier
      // on def.spriteSize). Default values mean no transform.
      tuning: {
        rotationDeg: Number(rotationDeg.toFixed(2)),
        rotationRad: Number((rotationDeg * Math.PI / 180).toFixed(4)),
        scaleFactor: Number(scaleFactor.toFixed(3)),
        // Suggestion: spriteRotationOffset is the NEGATIVE of the slider
        // rotation in radians (because in-engine the source-PNG angle is
        // canceled with `ctx.rotate(-spriteRotationOffset)`).
        suggestedSpriteRotationOffset: Number((-rotationDeg * Math.PI / 180).toFixed(4)),
        suggestedSpriteScale: Number(scaleFactor.toFixed(3)),
      },
      barrelCount: barrels.length,
      barrels,
      // BETA 22.69 — ready-to-paste WEAPON_VISUALS entry (first dot = muzzle
      // socket). weaponVisualConfig.ts consumes these PNG fractions directly.
      weaponVisualConfigSnippet: barrels.length > 0 ? {
        [weapon.id]: {
          muzzleFx: barrels[0].socketFraction.fx,
          muzzleFy: barrels[0].socketFraction.fy,
        },
      } : null,
      notes: barrels.length > 1
        ? 'Multi-barrel weapon. Each entry is one barrel — for fan-fire weapons (Ex Girlfriend, Hurray) PS Major derives spreadAngle and projectile fan from the dx/dy spread of the points.'
        : 'Single-barrel weapon. dx > 0 = barrel right of center. dy != 0 = sprite has vertical offset.',
    };
    return JSON.stringify(data, null, 2);
  }

  function handleCopy() {
    const json = buildCopyJson();
    if (!json) return;
    navigator.clipboard.writeText(json).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 1800); },
      () => { setCopied(false); }
    );
  }

  function handleClear() {
    setDots([]);
    setCopied(false);
  }

  function handleUndo() {
    setDots(prev => prev.slice(0, -1));
    setCopied(false);
  }

  // ── Styles ────────────────────────────────────────────────────────────────
  // BETA 14.07 — zIndex 9999 ensures the calibrator sits above the pause menu
  // (which renders on the canvas, zIndex < this). User: "this UI should not
  // be covered by the pause menu".
  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.85)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: '"Share Tech Mono", monospace',
    color: '#cfd8e3',
  };
  const modalStyle: React.CSSProperties = {
    background: 'rgba(8, 10, 14, 0.98)',
    border: '1px solid rgba(80, 200, 255, 0.4)',
    padding: 18,
    minWidth: 800,
    maxWidth: '95vw',
    maxHeight: '95vh',
    overflowY: 'auto',
    boxSizing: 'border-box',
  };
  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
    paddingBottom: 8,
    borderBottom: '1px solid rgba(80, 200, 255, 0.25)',
  };
  const btn: React.CSSProperties = {
    background: 'rgba(40, 80, 120, 0.5)',
    color: '#e6f2ff',
    border: '1px solid rgba(80, 200, 255, 0.4)',
    padding: '8px 14px',
    cursor: 'pointer',
    fontSize: 12,
    letterSpacing: '0.06em',
    fontFamily: 'inherit',
    borderRadius: 2,
  };
  const btnRed: React.CSSProperties = { ...btn, borderColor: '#ff5544', color: '#ffb0a4' };
  const btnGreen: React.CSSProperties = { ...btn, borderColor: '#44dd66', color: '#aaffb0' };
  const dataRow: React.CSSProperties = {
    fontSize: 11,
    color: '#cfd8e3',
    marginBottom: 3,
  };
  const labelStyle: React.CSSProperties = { color: '#7fdcff', display: 'inline-block', minWidth: 160 };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={headerStyle}>
          <div style={{ fontSize: 16, color: '#7fdcff', letterSpacing: '0.18em' }}>BARREL CALIBRATOR</div>
          <button style={btnRed} onClick={onClose} title="Close calibrator (game resumes)">✕ CLOSE</button>
        </div>

        {/* Weapon picker */}
        <div style={{ marginBottom: 12 }}>
          <span style={labelStyle}>WEAPON:</span>
          <select
            value={weaponId}
            onChange={e => {
              setWeaponId(e.target.value);
              handleClear();
              // BETA 14.18 — also reset sliders so each weapon starts fresh
              setRotationDeg(0);
              setScaleFactor(1.0);
            }}
            style={{ background: '#0a0d12', color: '#cfd8e3', border: '1px solid rgba(80,200,255,0.3)', padding: '6px 8px', minWidth: 260, fontFamily: 'inherit', fontSize: 12 }}
          >
            {allWeapons.map(w => (
              <option key={w.id} value={w.id}>{w.name} ({w.id})</option>
            ))}
          </select>
          <span style={{ marginLeft: 12, fontSize: 11, color: '#7fdcff' }}>
            {dots.length} barrel{dots.length === 1 ? '' : 's'} marked
          </span>
        </div>

        {/* Instructions */}
        <div style={{ fontSize: 11, color: '#7a8794', marginBottom: 8 }}>
          Click on each visible barrel tip. Multi-barrel weapons (e.g. Ex Girlfriend's 5-fan)
          get a numbered dot per click — keep clicking to add more. The cyan crosshair is the
          sprite center (slot anchor). Δ shows your click offset in canvas pixels. Game is
          paused while this tool is open. Copy the JSON and paste it back to PS Major.
        </div>

        {/* Canvas */}
        <div style={{ marginBottom: 12, textAlign: 'center' }}>
          <canvas
            ref={canvasRef}
            width={CANVAS_W}
            height={CANVAS_H}
            onClick={handleCanvasClick}
            style={{
              border: '1px solid rgba(80, 200, 255, 0.4)',
              cursor: 'crosshair',
              display: 'inline-block',
              maxWidth: '100%',
              height: 'auto',
            }}
          />
        </div>

        {/* BETA 14.18 — Live rotate + scale sliders */}
        <div style={{ background: 'rgba(0, 20, 40, 0.4)', padding: 12, marginBottom: 12, border: '1px solid rgba(80, 200, 255, 0.15)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <span style={{ ...labelStyle, minWidth: 80 }}>ROTATE:</span>
            <input
              type="range"
              min={-180}
              max={180}
              step={1}
              value={rotationDeg}
              onChange={e => setRotationDeg(parseFloat(e.target.value) || 0)}
              style={{ flex: 1, accentColor: '#7fdcff' }}
            />
            <span style={{ minWidth: 56, textAlign: 'right', fontSize: 11, color: '#7fdcff' }}>{rotationDeg.toFixed(0)}°</span>
            <button
              style={{ ...btn, padding: '2px 8px', fontSize: 10 }}
              onClick={() => setRotationDeg(0)}
              title="Reset rotation to 0°"
            >0°</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ ...labelStyle, minWidth: 80 }}>SCALE:</span>
            <input
              type="range"
              min={0.25}
              max={3.0}
              step={0.05}
              value={scaleFactor}
              onChange={e => setScaleFactor(parseFloat(e.target.value) || 1.0)}
              style={{ flex: 1, accentColor: '#7fdcff' }}
            />
            <span style={{ minWidth: 56, textAlign: 'right', fontSize: 11, color: '#7fdcff' }}>{scaleFactor.toFixed(2)}×</span>
            <button
              style={{ ...btn, padding: '2px 8px', fontSize: 10 }}
              onClick={() => setScaleFactor(1.0)}
              title="Reset scale to 1.0×"
            >1×</button>
          </div>
          <div style={{ fontSize: 10, color: '#6b7785', marginTop: 6 }}>
            ROTATE the sprite so the barrel points to the RIGHT (+X). SCALE adjusts
            visible size. Both values export in COPY DATA → PS Major applies as
            spriteRotationOffset + spriteScale on the weapon definition.
          </div>
        </div>

        {/* Data panel */}
        <div style={{ background: 'rgba(0, 20, 40, 0.4)', padding: 12, marginBottom: 12, border: '1px solid rgba(80, 200, 255, 0.15)' }}>
          <div style={dataRow}><span style={labelStyle}>weapon.id:</span> {weapon?.id ?? '—'}</div>
          <div style={dataRow}><span style={labelStyle}>weapon.name:</span> {weapon?.name ?? '—'}</div>
          <div style={dataRow}><span style={labelStyle}>spriteUrl:</span> {weapon?.spriteUrl ?? '—'}</div>
          <div style={dataRow}><span style={labelStyle}>spriteSize (world):</span> {weapon?.spriteSize ?? '—'} wu</div>
          <div style={dataRow}><span style={labelStyle}>current barrelLength:</span> {weapon?.barrelLength ?? '—'} wu</div>
          <div style={dataRow}><span style={labelStyle}>baseProjectileCount:</span> {weapon?.baseProjectileCount ?? '—'} (spreadAngle: {weapon?.spreadAngle ?? '—'}°)</div>
          <div style={{ ...dataRow, marginTop: 8 }}>
            <span style={labelStyle}>barrels marked:</span>{' '}
            {dots.length === 0 ? 'click on the sprite to mark barrel tip(s)' : `${dots.length}`}
          </div>
          {weapon ? dots.map((d, i) => (
            <div key={i} style={{ ...dataRow, marginLeft: 16 }}>
              <span style={{ color: '#ff8899', minWidth: 60, display: 'inline-block' }}>#{i + 1}:</span>{' '}
              Δ ({d.x - SLOT_X}px, {d.y - SLOT_Y}px) ={' '}
              ({((d.x - SLOT_X) / (SPRITE_DISPLAY_PX / Math.max(1, weapon.spriteSize))).toFixed(2)} wu, {((d.y - SLOT_Y) / (SPRITE_DISPLAY_PX / Math.max(1, weapon.spriteSize))).toFixed(2)} wu)
            </div>
          )) : null}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button style={btn} onClick={handleUndo} disabled={dots.length === 0} title="Remove the last dot">↩ UNDO</button>
          <button style={btn} onClick={handleClear} disabled={dots.length === 0} title="Remove all dots">↺ CLEAR ALL</button>
          <button
            style={dots.length > 0 ? btnGreen : btn}
            onClick={handleCopy}
            disabled={dots.length === 0}
            title={dots.length > 0 ? 'Copy JSON to clipboard' : 'Mark at least one barrel tip first'}
          >
            {copied ? '✓ COPIED' : '📋 COPY DATA'}
          </button>
          <button style={btn} onClick={onClose}>DONE</button>
        </div>
      </div>
    </div>
  );
}
